import {
  parse,
  parseISO,
  format as formatter,
  isValid,
  startOfDay,
  add,
  isBefore,
  isAfter,
  formatDistanceToNow,
  differenceInCalendarDays,
  isToday,
  isYesterday
} from "date-fns";
import { utcToZonedTime, format as zonedFormatter } from "date-fns-tz";

type PossibleDate = string | number | object;

const startsWithMonth = /^[^\d]*[a-z]{3}/i;
const includesMonth = /[a-z]{3}/i;

const timeZone = "America/New_York";

// Prints a date zoned to a different timezone
export const printZonedDate = (
  date: string,
  zone: string = timeZone
): string => {
  return zonedFormatter(getZonedDate(date, zone), "MM/dd/yyyy");
};

// returns a date object in UTC that represents a datetime in a specific timezone
export const getZonedDate = (date: string, zone: string = timeZone): Date => {
  return utcToZonedTime(date, zone);
};

// takes a time string like yyyy-mm-ddT00:00:00-04:00 and removes the time information
// everything after the T, and returns just the front date portion: yyyy-mm-dd
export const getDateWithoutTimezone = (date: string): string => {
  if (date?.includes("T")) {
    const dateArray = date.split("T");
    return dateArray[0];
  }

  return date;
};

const tryParse = (date: string, format: string): Date | null => {
  const parsedDate = parse(date, format, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

export const parseDate = (date: PossibleDate): Date | number | null => {
  if (typeof date === "number" || typeof date === "object") {
    return isValid(date) ? (date as Date) : null;
  }
  if (isValid(parseISO(date))) {
    return parseISO(date);
  }

  return [
    d =>
      startsWithMonth.test(d) ? tryParse(d, "MMMM, d yyyy HH:mm:ss") : null,
    d => (includesMonth.test(d) ? tryParse(d, "dd MMMM yyyy") : null),
    d => tryParse(d, "dd MMM yyyy h:mm:ss"),
    d => tryParse(d, "M/d/yy"),
    d => tryParse(d, "MM/dd/yyyy"),
    d => tryParse(d, "M/d/yyyy"),
    d => parseISO(d)
  ].reduce((acc, parser) => acc || parser(date), null as Date | number | null);
};

export const determineDateFormat = (date: string): string | null => {
  return [
    d =>
      startsWithMonth.test(d)
        ? tryParse(d, "MMMM, d yyyy HH:mm:ss")
          ? "MMMM, d yyyy HH:mm:ss"
          : null
        : null,
    d =>
      includesMonth.test(d)
        ? tryParse(d, "dd MMMM yyyy")
          ? "dd MMMM yyyy"
          : null
        : null,
    d => (tryParse(d, "dd MMM yyyy h:mm:ss") ? "dd MMM yyyy h:mm:ss" : null),
    d => (tryParse(d, "M/d/yy") ? "M/d/yy" : null),
    d => (tryParse(d, "MM/dd/yyyy") ? "MM/dd/yyyy" : null),
    d => (tryParse(d, "M/d/yyyy") ? "M/d/yyyy" : null),
    d => (tryParse(d, "yyyy-MM-dd") ? "yyyy-MM-dd" : null),
    d => (parseISO(d) ? "T" : null)
  ].reduce((acc, parser) => acc || parser(date), null as string | null);
};

export const printDate = (
  date: PossibleDate,
  format = "MM/dd/yyyy"
): string | null => {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) {
    return null;
  }
  return formatter(parsedDate, format);
};

export const printDistanceFromNow = (
  date: PossibleDate,
  future = false
): string | null => {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) return null;
  const formattedDate = formatDistanceToNow(parsedDate, { addSuffix: future });
  return future ? formattedDate : `${formattedDate} ago`;
};

export const validateEffectiveDate = (
  newDate: string | Date
): string | null => {
  const newDateParsed =
    typeof newDate === "string" ? parseDate(newDate) : newDate;
  if (!newDateParsed || !isValid(newDateParsed)) {
    return "Please choose a valid effective date (MM/DD/YYYY).";
  }

  const newDateStart = startOfDay(newDateParsed);
  const today = startOfDay(new Date());
  const nextYear = add(new Date(), { years: 1 });
  if (isBefore(newDateStart, today) || isAfter(newDateStart, nextYear)) {
    return `Please select a date between today and ${formatter(
      nextYear,
      "MM/dd/yyyy"
    )}.`;
  }
  return null;
};

export const getISOToday = () => new Date().toISOString().substring(0, 10);
export const getISOTimestamp = () => new Date().toISOString();

export const getLastUpdatedTime = (updateTimestamp): string => {
  // Date object from ISO string
  const parsedUpdateTimestamp = parseISO(updateTimestamp);
  const formattedUpdateTime = formatter(parsedUpdateTimestamp, "h:mm aaa"); // e.g. 5:05pm

  // if date is today
  if (isToday(parsedUpdateTimestamp)) {
    return `Last updated today, ${formattedUpdateTime}`;
  } else if (isYesterday(parsedUpdateTimestamp)) {
    return `Last updated yesterday, ${formattedUpdateTime}`;
  } else {
    const daysAgo = differenceInCalendarDays(
      new Date(),
      parseISO(updateTimestamp)
    );
    return `Last updated ${daysAgo} days ago, ${formattedUpdateTime}`;
  }
};

export const parseDateByType = (date: string, format: string) => {
  if (format === "ISO") {
    const dateWithoutTimezone = getDateWithoutTimezone(date);
    return parse(dateWithoutTimezone, "yyyy-MM-dd", new Date());
  }
  return parse(date, format, new Date());
};
